import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import Seo, { absUrl } from "../lib/seo";

function Block({ b }) {
  switch (b.type) {
    case "heading":
      return <h2 className="font-display font-600 uppercase tracking-[0.02em] text-2xl md:text-3xl mt-14 mb-4">{b.text}</h2>;
    case "subheading":
      return <h3 className="font-display uppercase tracking-[0.06em] text-lg mt-10 mb-3">{b.text}</h3>;
    case "paragraph":
      return <p className="text-lg leading-[1.8] mb-6 whitespace-pre-line">{b.text}</p>;
    case "quote":
    case "pullquote":
      return <blockquote className="border-l-2 pl-6 my-10 font-display text-xl md:text-2xl leading-snug" style={{ borderColor: "var(--ink)" }}>{b.text}</blockquote>;
    case "ul":
      return <ul className="list-disc pl-6 mb-6 space-y-2 text-lg leading-relaxed">{(b.items || []).map((it, i) => <li key={i}>{it}</li>)}</ul>;
    case "ol":
      return <ol className="list-decimal pl-6 mb-6 space-y-2 text-lg leading-relaxed">{(b.items || []).map((it, i) => <li key={i}>{it}</li>)}</ol>;
    case "link":
      return <p className="mb-6"><a href={b.url} target="_blank" rel="noreferrer" className="underline hover:opacity-60 transition-opacity">{b.text || b.url}</a></p>;
    case "divider":
      return <hr className="my-14 hairline" />;
    case "image":
      return (
        <figure className="my-10">
          <img src={b.url} alt={b.alt || b.caption || ""} loading="lazy" className="w-full h-auto bg-white border hairline" />
          {b.caption && <figcaption className="label mt-3">{b.caption}</figcaption>}
        </figure>
      );
    case "video":
    case "embed":
      return (
        <figure className="my-10">
          <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
            <iframe src={b.url} title={b.caption || "Embedded media"} loading="lazy" allowFullScreen
              className="absolute inset-0 w-full h-full border hairline" />
          </div>
          {b.caption && <figcaption className="label mt-3">{b.caption}</figcaption>}
        </figure>
      );
    default:
      return null;
  }
}

export default function PracticeDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setP(null); setErr(false);
    api.get(`/practice/${slug}`).then((r) => setP(r.data)).catch(() => setErr(true));
  }, [slug]);

  if (err) return (
    <main className="pt-52 text-center label min-h-[50vh]" data-testid="practice-notfound">
      <Seo title="Not found — RCKT" noindex />
      Entry not found — <Link to="/practice" className="underline">back to Practice</Link>
    </main>
  );
  if (!p) return <main className="pt-52 text-center label min-h-[50vh]">Loading…</main>;

  const canonical = `${window.location.origin}/practice/${p.slug}`;
  const jsonLd = {
    "@context": "https://schema.org", "@type": "Article",
    "headline": p.title, "url": canonical, "datePublished": p.date || undefined,
    "image": absUrl(p.eff_og_image) || undefined,
    "author": { "@type": "Person", "name": "RCKT" },
    "publisher": { "@type": "Person", "name": "RCKT" },
    "description": p.eff_seo_description,
  };

  return (
    <main data-testid="practice-detail-page" className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 pt-32 md:pt-40 pb-16">
      <Seo title={p.eff_seo_title} description={p.eff_seo_description} canonical={canonical}
        image={p.eff_og_image} type="article" noindex={p.status !== "published"} jsonLd={jsonLd} />
      <div className="border-b hairline pb-5 mb-12">
        <Link to="/practice" className="label hover:opacity-50 transition-opacity" data-testid="back-to-practice">← Practice</Link>
      </div>
      <article className="max-w-[760px] mx-auto">
        <div className="label mb-6">{[p.number, p.category, p.date].filter(Boolean).join(" · ")}</div>
        <h1 className="font-display font-700 tracking-[-0.02em] leading-[1.05] text-4xl md:text-6xl">{p.title}</h1>
        {p.subtitle && <p className="mt-6 text-xl md:text-2xl text-[var(--muted)] leading-snug">{p.subtitle}</p>}
        <div className="mt-14">
          {(p.blocks || []).map((b, i) => <Block key={i} b={b} />)}
        </div>
        {p.tags?.length > 0 && (
          <div className="mt-16 pt-6 border-t hairline flex flex-wrap gap-3">
            {p.tags.map((t) => <span key={t} className="label">#{t}</span>)}
          </div>
        )}
      </article>
    </main>
  );
}
